// The fake api's organisation Players and wallet routes (spec/13 §8, F-ORG-05/-06, F-IAM-07,
// F-CMP-02; spec/06 §5, F-WAL-01/-02/-08), in the contract's shapes — enough for the Players screen and
// its player sheet (overview, wallet with its top-up, notes), the floor's player names and every player
// picker to be rendered against the fake in `fake-server.ts`, at a venue (`/v/playground-central/…`) and at
// the organisation (`/o/playground/…`).
//
//   GET   …/organizations/{o}/players?q=&tag=&memberQr=       listPlayers         a page of players
//   GET   …/players/{p}                                        getPlayer           profile, tags, notes
//   PATCH …/players/{p}                                        updatePlayer        tags and notes
//   GET   …/players/{p}/bans                                   listPlayerBans      newest first
//   POST  …/venues/{v}/players/{p}/ban|unban · …/players/{p}/ban|unban            ban, lift
//   GET   …/players/{p}/id-checks                              listIdChecks        newest first
//   POST  …/players/{p}/id-checks                              recordIdCheck       a staff document check (F-CMP-02)
//   GET   …/players/{p}/wallet/balance                         getWalletBalance    cash, bonus, total, useForPlay
//   GET   …/players/{p}/wallet                                 listPlayerWallet    the statement
//   POST  …/players/{p}/wallet/top-ups                         topUpWallet         a counter top-up
//   POST  …/players/{p}/wallet/adjustments                     adjustWallet        a staff credit or debit
//   POST  …/players/{p}/wallet/refunds                         refundWallet        cash back to a counter tender
//
// Ten players of Playground Gaming, contacts masked as a role without `players:read_contact` reads
// them (spec/07 §3.5): one with a staff document check (spec/07 §10), one banned at the venue with an
// older, lifted organisation ban, one with more tags than the list shows, and wallets whose statement
// lines sum to the balance — cash and bonus kept apart, integer paise throughout (spec/06 §1, §5). Every
// player lets the wallet pay for play time (F-WAL-04) but rohang, who turned it off at the kiosk.
// Queries and bodies are checked with the contract's own schemas. The permission guard is the
// caller's: this file answers as a member who holds the route's permission would be answered.
//
// The wallet's refusals are the server's (apps/server/src/modules/wallet/routes.ts), each named by its catalogue slug
// and answered with the status, title and snake_case `code` the contract derives from it (`refuse`), never a code
// written by hand, so a console test against this fake meets exactly what production sends (2026-10-12: the fake
// answered `wallet-insufficient-cash`, the console matched that, and production's `wallet_insufficient_cash` fell
// through to "refused"). The fixtures' organisation runs a wallet unless a test turns `wallet.enabled` off; a top-up is
// held to the registry's default limits (₹100 to ₹10,000); cash needs the caller's own open drawer (ADR-0031).

import type {
  IdCheck,
  OrganizationPlayer,
  OrganizationPlayerProfile,
  PlayerBan,
  PlayerNote,
  PlayerWalletEntry,
  Problem,
  WalletBalance,
  WalletTransaction,
} from '@clavian/api-client';
import {
  BanPlayerSchema,
  errorCode,
  type ErrorSlug,
  errorStatus,
  errorTitle,
  LiftBanSchema,
  RecordIdCheckSchema,
  normalisePlayerTag,
  PageQuerySchema,
  PlayerHistoryPageQuerySchema,
  PlayerSearchQuerySchema,
  UpdateOrganizationPlayerSchema,
  WalletAdjustRequestSchema,
  WalletRefundRequestSchema,
  WalletTopUpRequestSchema,
  type z,
} from '@clavian/contracts';

import type { FakeResponse } from './fake-server';

const ORGANIZATION_ID = 'org_01k5znw4hmfay8vvr14d2pf2db';
const VENUE_ID = 'ven_01k5znw4hmfay8vvr14d2pf2dc';
const NOW = '2026-09-26T14:00:00.000Z';
const INR = (amount: number) => ({ amount, currency: 'INR' });

export interface PlayersHelpers {
  readonly json: (status: number, body: unknown, headers?: Record<string, string>) => FakeResponse;
  readonly problem: (status: number, code: string, title: string, extra?: Partial<Problem>) => FakeResponse;
  /** The console gate (a session, and a second factor where one is required): a refusal, or `undefined`. */
  readonly gate: () => FakeResponse | undefined;
  /** A setting as the fake resolves it (`wallet.enabled`, `wallet.cashRefundsAllowed`); absent, the fixtures' own. */
  readonly setting?: (key: string) => unknown;
  /** Whether cash taken now would be refused for want of an open drawer (ADR-0031): the till's fake knows. */
  readonly cashNeedsShift?: () => boolean;
}

/** The players' ids, for tests that open one. `aarav` is `fake-server.ts`'s `ORG_PLAYER`. */
export const FAKE_PLAYER_IDS = {
  aarav: 'ply_01k5znw4hmfay8vvr14d2pf2a1',
  diya: 'ply_01k5znw4hmfay8vvr14d2pf5b1',
  kabir: 'ply_01k5znw4hmfay8vvr14d2pf5b2',
  ananya: 'ply_01k5znw4hmfay8vvr14d2pf5b3',
  rohan: 'ply_01k5znw4hmfay8vvr14d2pf5b4',
  ishaan: 'ply_01k5znw4hmfay8vvr14d2pf5b5',
  meera: 'ply_01k5znw4hmfay8vvr14d2pf5b6',
  vihaan: 'ply_01k5znw4hmfay8vvr14d2pf5b7',
  saanvi: 'ply_01k5znw4hmfay8vvr14d2pf5b8',
  arjun: 'ply_01k5znw4hmfay8vvr14d2pf5b9',
} as const;

/** A player as the fake keeps them: the organisation's view, plus the full contact search matches on. */
interface StoredPlayer {
  readonly player: OrganizationPlayer;
  /** E.164 and the address in full: `?q=` matches them whole, as the server does (spec/13 §8 rule 6). */
  readonly contact: { readonly phone: string | null; readonly email: string | null };
  notes: PlayerNote[];
}

function maskPhone(phone: string | null): string | null {
  return phone === null ? null : `•••${phone.slice(-3)}`;
}

function maskEmail(email: string | null): string | null {
  if (email === null) return null;
  const [local = '', domain = ''] = email.split('@');
  return `${local.slice(0, 1)}•••@${domain}`;
}

function stored(
  id: string,
  who: { username?: string; displayName?: string; firstName: string | null; lastName: string | null },
  contact: { phone: string | null; email: string | null },
  tags: readonly string[],
  knownSince: string,
  notes: PlayerNote[] = [],
): StoredPlayer {
  return {
    player: {
      id,
      username: who.username ?? null,
      displayName: who.displayName ?? null,
      firstName: who.firstName,
      lastName: who.lastName,
      phoneNumber: maskPhone(contact.phone),
      email: maskEmail(contact.email),
      contactMasked: true,
      tags: [...tags].sort(),
      knownSince,
    },
    contact,
    notes,
  };
}

const note = (id: string, body: string, name: string | null, createdAt: string): PlayerNote => ({ id, body, author: { name }, createdAt, updatedAt: createdAt });

function seedPlayers(): StoredPlayer[] {
  const ids = FAKE_PLAYER_IDS;
  return [
    // `ORG_PLAYER` in fake-server.ts, field for field.
    stored(ids.aarav, { firstName: 'Aarav', lastName: 'Mehta' }, { phone: '+919876543210', email: 'aarav.mehta@example.com' }, ['regular'], '2026-01-05T10:00:00.000Z'),
    stored(
      ids.diya,
      { username: 'diyarao', firstName: 'Diya', lastName: 'Rao' },
      { phone: '+919812345678', email: 'diya.rao@example.com' },
      ['regular', 'tournament'],
      '2026-02-14T11:30:00.000Z',
      [
        note('pnt_01k5znw4hmfay8vvr14d2pf5c2', 'Captain of the Friday Valorant team; books PC 7–11 for scrims.', 'Riya Sharma', '2026-09-24T17:45:00.000Z'),
        note('pnt_01k5znw4hmfay8vvr14d2pf5c1', 'Prefers the 240 Hz monitors. Ask before moving her to the console zone.', null, '2026-08-02T12:10:00.000Z'),
      ],
    ),
    stored(
      ids.kabir,
      { username: 'kabir_s', firstName: 'Kabir', lastName: 'Singh' },
      { phone: '+919900112233', email: 'kabir.singh@example.com' },
      ['watch list'],
      '2026-03-01T15:00:00.000Z',
      [note('pnt_01k5znw4hmfay8vvr14d2pf5c3', 'Banned at Central after the headset incident on 20 September. See the ban reason.', 'Riya Sharma', '2026-09-20T19:05:00.000Z')],
    ),
    stored(ids.ananya, { username: 'ananya_i', displayName: 'Ananya I', firstName: 'Ananya', lastName: 'Iyer' }, { phone: null, email: 'ananya.iyer@example.com' }, ['student'], '2026-04-18T09:20:00.000Z'),
    stored(
      ids.rohan,
      { username: 'rohang', firstName: 'Rohan', lastName: 'Gupta' },
      { phone: '+919845098450', email: 'rohan.gupta@example.com' },
      ['night owl', 'pays cash', 'regular', 'student', 'tournament'],
      '2026-01-20T20:00:00.000Z',
    ),
    stored(ids.ishaan, { firstName: 'Ishaan', lastName: 'Nair' }, { phone: '+919744556677', email: 'ishaan.nair@example.com' }, [], '2026-06-07T13:15:00.000Z'),
    stored(ids.meera, { firstName: 'Meera', lastName: 'Krishnan' }, { phone: '+919633221100', email: null }, [], '2026-07-11T16:40:00.000Z'),
    stored(ids.vihaan, { username: 'vrx', displayName: 'VRX', firstName: 'Vihaan', lastName: 'Reddy' }, { phone: '+919000011122', email: 'vihaan.reddy@example.com' }, ['tournament'], '2026-05-03T18:25:00.000Z'),
    stored(ids.saanvi, { username: 'saanvi.j', firstName: 'Saanvi', lastName: 'Joshi' }, { phone: '+919811122233', email: 'saanvi.joshi@example.com' }, ['birthday party'], '2026-08-29T10:05:00.000Z'),
    stored(ids.arjun, { firstName: 'Arjun', lastName: 'Banerjee' }, { phone: '+919830098300', email: 'arjun.banerjee@example.com' }, [], '2026-09-25T12:00:00.000Z'),
  ];
}

function seedBans(): PlayerBan[] {
  const base = { organizationId: ORGANIZATION_ID, playerId: FAKE_PLAYER_IDS.kabir };
  return [
    {
      ...base,
      id: 'ban_01k5znw4hmfay8vvr14d2pf5d2',
      scope: 'venue',
      venueId: VENUE_ID,
      reason: 'Damaged a headset at PC 4 and was abusive to the floor staff when asked to pay for it.',
      expiresAt: '2026-10-20T18:30:00.000Z',
      status: 'active',
      bannedAt: '2026-09-20T19:00:00.000Z',
      updatedAt: '2026-09-20T19:00:00.000Z',
      liftedAt: null,
      liftReason: null,
    },
    {
      ...base,
      id: 'ban_01k5znw4hmfay8vvr14d2pf5d1',
      scope: 'organization',
      venueId: null,
      reason: 'Shared his account with a minor during school hours.',
      expiresAt: null,
      status: 'lifted',
      bannedAt: '2026-04-02T11:00:00.000Z',
      updatedAt: '2026-04-16T10:00:00.000Z',
      liftedAt: '2026-04-16T10:00:00.000Z',
      liftReason: 'Parent came in and confirmed the account is his own again.',
    },
  ];
}

function seedIdChecks(): IdCheck[] {
  const base = { organizationId: ORGANIZATION_ID, method: 'staff_document' as const };
  return [
    {
      ...base,
      id: 'idc_01k5znw4hmfay8vvr14d2pf5e1',
      playerId: FAKE_PLAYER_IDS.diya,
      document: { type: 'aadhaar', last4: '4821' },
      verifiedBy: { name: 'Riya Sharma' },
      verifiedAt: '2026-09-14T12:20:00.000Z',
      photo: { contentType: 'image/jpeg', deletedAt: null },
    },
    {
      ...base,
      id: 'idc_01k5znw4hmfay8vvr14d2pf5e2',
      playerId: FAKE_PLAYER_IDS.rohan,
      document: { type: 'pan', last4: '123F' },
      verifiedBy: { name: null },
      verifiedAt: '2026-07-02T09:45:00.000Z',
      // Past `compliance.idPhotoRetentionDays`: the photo is gone, the check stays.
      photo: { contentType: 'image/jpeg', deletedAt: '2026-08-01T09:45:00.000Z' },
    },
  ];
}

/** The fixtures' `wallet.maxBalanceAmount` — the registry default, ₹20,000 (spec/06 §5). */
export const WALLET_MAX_BALANCE_MINOR = 2_000_000;
/** The fixtures' `wallet.minTopupAmount` and `wallet.maxTopupAmount` — the registry defaults, ₹100 and ₹10,000. */
export const WALLET_MIN_TOPUP_MINOR = 10_000;
export const WALLET_MAX_TOPUP_MINOR = 1_000_000;

/** A movement on a wallet, as the fake keeps it: cash and bonus apart, so the balance is their sum. */
interface Movement {
  readonly id: string;
  readonly occurredAt: string;
  readonly cash: number;
  readonly bonus: number;
  readonly description: string;
}

const move = (suffix: string, occurredAt: string, cash: number, bonus: number, description: string): Movement => ({
  id: `wtx_01k5znw4hmfay8vvr14d2pf6${suffix}`,
  occurredAt,
  cash,
  bonus,
  description,
});

/** Oldest first. Bonus is spent before cash (spec/06 §5). A player with no entry has no wallet yet. */
function seedWallets(): Map<string, Movement[]> {
  return new Map<string, Movement[]>([
    [
      FAKE_PLAYER_IDS.diya,
      [
        move('a1', '2026-09-10T10:00:00.000Z', 100_000, 0, 'Top-up (cash)'),
        move('a2', '2026-09-10T10:00:00.000Z', 0, 10_000, 'Top-up bonus: ₹1,000 gets ₹100'),
        move('a3', '2026-09-12T15:30:00.000Z', -14_000, -10_000, 'Play at PC 7 — 2 h'),
        move('a4', '2026-09-15T18:10:00.000Z', -18_000, 0, 'Snacks at the counter'),
        move('a5', '2026-09-18T11:00:00.000Z', 12_000, 0, 'Refund: PC 3 went down mid-session'),
        move('a6', '2026-09-22T19:40:00.000Z', -36_000, 0, 'Play at PC 12 — 3 h'),
        move('a7', '2026-09-24T17:50:00.000Z', 5_000, 0, 'Adjustment: goodwill credit for the queue wait'),
        move('a8', '2026-09-26T13:30:00.000Z', 50_000, 0, 'Top-up (UPI at the counter)'),
        move('a9', '2026-09-26T13:30:00.000Z', 0, 5_000, 'Top-up bonus: ₹500 gets ₹50'),
      ],
    ],
    [
      FAKE_PLAYER_IDS.aarav,
      [move('b1', '2026-09-20T16:00:00.000Z', 50_000, 0, 'Top-up (cash)'), move('b2', '2026-09-20T16:05:00.000Z', -15_000, 0, 'Play at PC 2 — 1 h 15 min')],
    ],
    [
      FAKE_PLAYER_IDS.kabir,
      [move('c1', '2026-09-19T14:00:00.000Z', 30_000, 0, 'Top-up (card at the counter)'), move('c2', '2026-09-20T18:00:00.000Z', -12_000, 0, 'Play at PC 4 — 1 h')],
    ],
    [
      FAKE_PLAYER_IDS.rohan,
      [
        move('d1', '2026-09-01T21:00:00.000Z', 200_000, 0, 'Top-up (cash)'),
        move('d2', '2026-09-01T21:00:00.000Z', 0, 30_000, 'Top-up bonus: ₹2,000 gets ₹300'),
        move('d3', '2026-09-25T23:30:00.000Z', -20_000, -30_000, 'Night pass — 6 h'),
      ],
    ],
  ]);
}

/**
 * "Use my wallet for play time" (F-WAL-04, spec/05 §4.1): the players who turned it off. Everyone else has it on — the
 * default, with or without a wallet yet. Only the player changes it, at the kiosk; the console reads it.
 */
function seedWalletOff(): Set<string> {
  return new Set([FAKE_PLAYER_IDS.rohan]);
}

/** TypeID's base32 (Crockford, lower case): no i, l, o or u. */
const TYPEID_ALPHABET = '0123456789abcdefghjkmnpqrstvwxyz';

const PLAYER_ROUTE =
  /^\/v1\/organizations\/([^/]+)\/(?:venues\/([^/]+)\/)?players(?:\/([^/]+)(?:\/(bans|id-checks|wallet|wallet\/balance|wallet\/top-ups|wallet\/adjustments|wallet\/refunds|ban|unban|sessions|games|orders|passes|points))?)?$/;

/** The fixtures' `wallet.adjustMaxAmount` for every role: ₹2,000 a go (spec/07 §6.4). */
const WALLET_ADJUST_MAX_MINOR = 200_000;

/** A cursor for the fake: the offset of the next row, as a string. The real one is opaque. */
function pageOf<T>(rows: readonly T[], limit: number, cursor: string | undefined) {
  const start = cursor === undefined ? 0 : Number.parseInt(cursor, 10) || 0;
  const data = rows.slice(start, start + limit);
  const next = start + data.length;
  const hasMore = next < rows.length;
  return { data, page: { cursor: hasMore ? String(next) : null, hasMore, count: data.length } };
}

export function createFakePlayers(helpers: PlayersHelpers) {
  const { json, problem } = helpers;
  const state = {
    players: seedPlayers(),
    bans: seedBans(),
    idChecks: seedIdChecks(),
    wallets: seedWallets(),
    /** The players who turned "Use my wallet for play time" off (F-WAL-04). */
    walletOff: seedWalletOff(),
    /** `false` answers the balance as a server from before F-WAL-04 does: without `useForPlay`. */
    reportsWalletUse: true,
    made: 0,
  };

  const invalid = (error: z.ZodError) =>
    problem(400, 'validation_failed', 'Validation failed', {
      detail: 'The request could not be understood as written.',
      errors: error.issues.map((issue) => ({ field: issue.path.map(String).join('.') || '(request)', code: issue.code, message: issue.message })),
    });
  const notFound = () => problem(404, 'not_found', 'No such player among this organisation’s players.');
  /** A refusal as the server sends it: status, title and code all from the contract's catalogue entry for `slug`. */
  const refuse = (slug: ErrorSlug, detail?: string) => problem(errorStatus(slug), errorCode(slug), errorTitle(slug), detail === undefined ? {} : { detail });
  /** The organisation runs a wallet in the fixtures; a test turns it off with `wallet.enabled: false`. */
  const walletEnabled = () => helpers.setting?.('wallet.enabled') !== false;
  /** Rupees in a refusal's words, as the server writes them (`moneyText`): ₹100, ₹10,000, ₹50.50. */
  const rupees = (minor: number) =>
    `₹${(minor / 100).toLocaleString('en-IN', { minimumFractionDigits: minor % 100 === 0 ? 0 : 2, maximumFractionDigits: 2 })}`;
  /** A fresh id for a row the fake makes: the seed's shape, a counter in its last characters. */
  const freshId = (prefix: string) => {
    const n = state.made++;
    return `${prefix}_01k5znw4hmfay8vvr14d2pf7${TYPEID_ALPHABET[Math.floor(n / 32) % 32] ?? '0'}${TYPEID_ALPHABET[n % 32] ?? '0'}`;
  };

  const profile = (entry: StoredPlayer): OrganizationPlayerProfile => ({ ...entry.player, notes: [...entry.notes] });

  function balance(playerId: string): WalletBalance {
    const movements = state.wallets.get(playerId) ?? [];
    const cash = movements.reduce((sum, row) => sum + row.cash, 0);
    const bonus = movements.reduce((sum, row) => sum + row.bonus, 0);
    return { playerId, cash: INR(cash), bonus: INR(bonus), total: INR(cash + bonus), enabled: walletEnabled() };
  }

  function statement(playerId: string): PlayerWalletEntry[] {
    return [...(state.wallets.get(playerId) ?? [])]
      .reverse()
      .map((row) => ({ id: row.id, occurredAt: row.occurredAt, amount: INR(row.cash + row.bonus), description: row.description }));
  }

  function listPlayers(query: URLSearchParams): FakeResponse {
    const parsed = PlayerSearchQuerySchema.safeParse(Object.fromEntries(query));
    if (!parsed.success) return invalid(parsed.error);
    const { q, tag, memberQr, limit, cursor } = parsed.data;
    const needle = q?.toLowerCase();
    const wanted = tag === undefined ? undefined : normalisePlayerTag(tag);
    const rows = state.players
      .filter(({ player, contact }) => {
        if (memberQr !== undefined && player.id !== memberQr) return false;
        if (wanted !== undefined && !player.tags.includes(wanted)) return false;
        if (needle === undefined || q === undefined) return true;
        // A phone or an email is matched whole; anything else against the names (spec/13 §8 rule 6).
        if (q.startsWith('+')) return contact.phone === q;
        if (q.includes('@')) return contact.email?.toLowerCase() === needle;
        return [player.firstName, player.lastName, player.displayName, player.username, [player.firstName, player.lastName].join(' ')].some(
          (name) => name !== null && name.toLowerCase().includes(needle),
        );
      })
      .map(({ player }) => player)
      // Newest dealings first.
      .sort((a, b) => b.knownSince.localeCompare(a.knownSince));
    return json(200, pageOf(rows, limit, cursor));
  }

  function updatePlayer(entry: StoredPlayer, input: Record<string, unknown>): FakeResponse {
    const parsed = UpdateOrganizationPlayerSchema.safeParse(input);
    if (!parsed.success) return invalid(parsed.error);
    const { tags, notes } = parsed.data;
    const nextTags = new Set(entry.player.tags);
    for (const tag of tags?.remove ?? []) nextTags.delete(normalisePlayerTag(tag));
    for (const tag of tags?.add ?? []) nextTags.add(normalisePlayerTag(tag));
    const removed = new Set<string>(notes?.remove ?? []);
    const edits = new Map<string, string>((notes?.edit ?? []).map((edit) => [edit.id, edit.body]));
    const kept = entry.notes.filter((row) => !removed.has(row.id)).map((row) => (edits.has(row.id) ? { ...row, body: edits.get(row.id) ?? row.body, updatedAt: NOW } : row));
    const added = (notes?.add ?? []).map((row) => note(freshId('pnt'), row.body, 'Riya Sharma', NOW));
    const index = state.players.indexOf(entry);
    const next: StoredPlayer = { ...entry, player: { ...entry.player, tags: [...nextTags].sort() }, notes: [...added, ...kept] };
    state.players[index] = next;
    return json(200, profile(next));
  }

  function ban(playerId: string, venueId: string | undefined, input: Record<string, unknown>): FakeResponse {
    const parsed = BanPlayerSchema.safeParse(input);
    if (!parsed.success) return invalid(parsed.error);
    const scope = venueId === undefined ? 'organization' : 'venue';
    const held = state.bans.find((row) => row.playerId === playerId && row.scope === scope && row.venueId === (venueId ?? null) && row.status === 'active');
    if (held !== undefined) {
      const changed: PlayerBan = { ...held, reason: parsed.data.reason, expiresAt: parsed.data.expiresAt ?? held.expiresAt, updatedAt: NOW };
      state.bans = state.bans.map((row) => (row.id === held.id ? changed : row));
      return json(200, changed);
    }
    const made: PlayerBan = {
      id: freshId('ban'),
      organizationId: ORGANIZATION_ID,
      scope,
      venueId: venueId ?? null,
      playerId,
      reason: parsed.data.reason,
      expiresAt: parsed.data.expiresAt ?? null,
      status: 'active',
      bannedAt: NOW,
      updatedAt: NOW,
      liftedAt: null,
      liftReason: null,
    };
    state.bans = [made, ...state.bans];
    return json(201, made);
  }

  function unban(playerId: string, venueId: string | undefined, input: Record<string, unknown>): FakeResponse {
    const parsed = LiftBanSchema.safeParse(input);
    if (!parsed.success) return invalid(parsed.error);
    const scope = venueId === undefined ? 'organization' : 'venue';
    const held = state.bans.find((row) => row.playerId === playerId && row.scope === scope && row.venueId === (venueId ?? null) && row.status !== 'lifted');
    if (held === undefined) return problem(404, 'not_found', 'No ban on record here.');
    const lifted: PlayerBan = { ...held, status: 'lifted', liftedAt: NOW, updatedAt: NOW, liftReason: parsed.data.reason };
    state.bans = state.bans.map((row) => (row.id === held.id ? lifted : row));
    return json(200, lifted);
  }

  function topUp(playerId: string, input: Record<string, unknown>): FakeResponse {
    const parsed = WalletTopUpRequestSchema.safeParse(input);
    if (!parsed.success) return invalid(parsed.error);
    const { amount, tender } = parsed.data;
    // As the server: only the payment provider records an online top-up (spec/06 §9.3, F-PAY-03).
    if (tender === 'online') return refuse('validation-failed', 'Begin an online top-up at …/wallet/online-top-ups.');
    if (!walletEnabled()) return refuse('wallet-disabled', 'The organisation has not enabled its wallet.');
    if (amount.currency !== 'INR') return refuse('validation-failed', `The top-up is ${amount.currency}, the organisation trades in INR.`);
    if (amount.amount < WALLET_MIN_TOPUP_MINOR) return refuse('wallet-limit', `The smallest top-up is ${rupees(WALLET_MIN_TOPUP_MINOR)}.`);
    if (amount.amount > WALLET_MAX_TOPUP_MINOR) return refuse('wallet-limit', `The largest single top-up is ${rupees(WALLET_MAX_TOPUP_MINOR)}.`);
    // Cash goes into the taker's own open drawer (ADR-0031): none, and it is refused before anything is written.
    if (tender === 'cash' && helpers.cashNeedsShift?.() === true) {
      return refuse('cash-needs-shift', 'You hold no open drawer here: start a shift to take cash, or take card or UPI.');
    }
    const label = { cash: 'cash', counter_card: 'card at the counter', counter_upi: 'UPI at the counter', online: 'online' }[tender];
    const row = { id: freshId('wtx'), occurredAt: NOW, cash: amount.amount, bonus: 0, description: `Top-up (${label})` };
    state.wallets.set(playerId, [...(state.wallets.get(playerId) ?? []), row]);
    const posted: WalletTransaction = {
      id: row.id,
      kind: 'topup',
      occurredAt: NOW,
      cashAmount: INR(row.cash),
      bonusAmount: INR(0),
      description: row.description,
      ledgerEntryId: freshId('jen'),
    };
    return json(201, { wallet: balance(playerId), topUp: posted, bonus: null });
  }

  /** A posted movement, in the contract's shape. */
  const movement = (row: { id: string; cash: number; bonus: number; description: string }, kind: WalletTransaction['kind']): WalletTransaction => ({
    id: row.id,
    kind,
    occurredAt: NOW,
    cashAmount: INR(row.cash),
    bonusAmount: INR(row.bonus),
    description: row.description,
    ledgerEntryId: freshId('jen'),
  });

  /** Staff credit or debit the cash with a reason (F-WAL-07): capped per go, and a debit never overdraws the cash. */
  function adjust(playerId: string, input: Record<string, unknown>): FakeResponse {
    const parsed = WalletAdjustRequestSchema.safeParse(input);
    if (!parsed.success) return invalid(parsed.error);
    const { direction, amount, reason } = parsed.data;
    if (!walletEnabled()) return refuse('wallet-disabled', 'The organisation has not enabled its wallet.');
    if (amount.amount > WALLET_ADJUST_MAX_MINOR) return refuse('wallet-adjustment-over-limit', `A wallet adjustment here is capped at ${rupees(WALLET_ADJUST_MAX_MINOR)}.`);
    if (direction === 'debit' && amount.amount > balance(playerId).cash.amount) return refuse('wallet-insufficient-cash', 'The wallet does not hold that much cash.');
    const signed = direction === 'credit' ? amount.amount : -amount.amount;
    const row = { id: freshId('wtx'), occurredAt: NOW, cash: signed, bonus: 0, description: `Adjustment: ${reason}` };
    state.wallets.set(playerId, [...(state.wallets.get(playerId) ?? []), row]);
    return json(201, { wallet: balance(playerId), adjustment: movement(row, 'adjustment') });
  }

  /** Pay wallet cash back to a counter tender with a reason (F-WAL-06): never the bonus, never more than the cash. */
  function refund(playerId: string, input: Record<string, unknown>): FakeResponse {
    const parsed = WalletRefundRequestSchema.safeParse(input);
    if (!parsed.success) return invalid(parsed.error);
    const { amount, reason } = parsed.data;
    if (!walletEnabled()) return refuse('wallet-disabled', 'The organisation has not enabled its wallet.');
    if (helpers.setting?.('wallet.cashRefundsAllowed') === false) return refuse('wallet-refunds-off', 'The organisation does not allow wallet cash refunds.');
    if (amount.amount > balance(playerId).cash.amount) return refuse('wallet-insufficient-cash', 'The wallet does not hold that much cash.');
    const row = { id: freshId('wtx'), occurredAt: NOW, cash: -amount.amount, bonus: 0, description: `Refund: ${reason}` };
    state.wallets.set(playerId, [...(state.wallets.get(playerId) ?? []), row]);
    return json(201, { wallet: balance(playerId), refund: movement(row, 'refund') });
  }

  /**
   * Credit a wallet with an online top-up the provider captured (F-WAL-02, F-PAY-03). Called by the
   * payments fake when a test captures an intent, never by a route.
   */
  function creditOnline(playerId: string, amount: number): void {
    const row = { id: freshId('wtx'), occurredAt: NOW, cash: amount, bonus: 0, description: 'Top-up (online)' };
    state.wallets.set(playerId, [...(state.wallets.get(playerId) ?? []), row]);
  }

  /**
   * Draw a wallet for a sale's `wallet` tender (F-WAL-04): bonus first, then cash (spec/06 §5), or
   * `insufficient` when the balance is short. Called by the commerce fake, not a route of its own.
   */
  function spend(playerId: string, amount: number, description: string): 'ok' | 'insufficient' {
    const held = balance(playerId);
    if (amount > held.total.amount) return 'insufficient';
    const fromBonus = Math.min(held.bonus.amount, amount);
    const row = { id: freshId('wtx'), occurredAt: NOW, cash: -(amount - fromBonus), bonus: -fromBonus, description };
    state.wallets.set(playerId, [...(state.wallets.get(playerId) ?? []), row]);
    return 'ok';
  }

  /**
   * Credit wallet cash with a sale refunded to the wallet (spec/06 §8.7, 2026-10-02), or `over-balance` when the new
   * money would take the wallet over the fixtures' `wallet.maxBalanceAmount` (₹20,000). Called by the commerce fake.
   */
  function creditRefund(playerId: string, amount: number, newMoney: number, description: string): 'ok' | 'over-balance' {
    if (balance(playerId).total.amount + newMoney > WALLET_MAX_BALANCE_MINOR) return 'over-balance';
    const row = { id: freshId('wtx'), occurredAt: NOW, cash: amount, bonus: 0, description };
    state.wallets.set(playerId, [...(state.wallets.get(playerId) ?? []), row]);
    return 'ok';
  }

  /** Add a wallet bonus, as a redeemed coupon does (F-POS-06). Called by the promotions fake. */
  function grantBonus(playerId: string, amount: number): void {
    const row = { id: freshId('wtx'), occurredAt: NOW, cash: 0, bonus: amount, description: 'Coupon bonus' };
    state.wallets.set(playerId, [...(state.wallets.get(playerId) ?? []), row]);
  }

  /** `undefined` for a request that is not one of these routes. */
  function handle(method: string, pathname: string, query: URLSearchParams, input: Record<string, unknown>): FakeResponse | undefined {
    const match = PLAYER_ROUTE.exec(pathname);
    if (match === null) return undefined;
    const [, organizationId, venueId, playerId, tail] = match;
    if (organizationId !== ORGANIZATION_ID) return undefined;
    // The venue-scoped path carries only a ban and its lift (player-bans.ts).
    if (venueId !== undefined && (tail !== 'ban' && tail !== 'unban')) return undefined;

    const refused = helpers.gate();
    if (refused !== undefined) return refused;

    if (playerId === undefined) return method === 'GET' ? listPlayers(query) : undefined;
    if (venueId !== undefined && venueId !== VENUE_ID) return problem(404, 'not_found', 'No such venue.');
    const entry = state.players.find(({ player }) => player.id === playerId);
    if (entry === undefined) return notFound();

    const page = (schema: typeof PageQuerySchema | typeof PlayerHistoryPageQuerySchema, rows: readonly unknown[]) => {
      const parsed = schema.safeParse(Object.fromEntries(query));
      return parsed.success ? json(200, pageOf(rows, parsed.data.limit, parsed.data.cursor)) : invalid(parsed.error);
    };

    switch (`${method} ${tail ?? ''}`) {
      case 'GET ':
        return json(200, profile(entry));
      case 'PATCH ':
        return updatePlayer(entry, input);
      case 'GET bans':
        return page(
          PageQuerySchema,
          state.bans.filter((row) => row.playerId === playerId).sort((a, b) => b.bannedAt.localeCompare(a.bannedAt)),
        );
      case 'POST ban':
        return ban(playerId, venueId, input);
      case 'POST unban':
        return unban(playerId, venueId, input);
      case 'GET id-checks':
        return page(
          PageQuerySchema,
          state.idChecks.filter((row) => row.playerId === playerId).sort((a, b) => b.verifiedAt.localeCompare(a.verifiedAt)),
        );
      case 'POST id-checks': {
        const parsed = RecordIdCheckSchema.safeParse(input);
        if (!parsed.success) return invalid(parsed.error);
        const made: IdCheck = {
          id: freshId('idc'),
          organizationId: ORGANIZATION_ID,
          playerId,
          method: 'staff_document',
          document: { type: parsed.data.documentType, last4: parsed.data.documentLast4.toUpperCase() },
          verifiedBy: { name: 'Riya Sharma' },
          verifiedAt: NOW,
          photo: parsed.data.photo === undefined ? null : { contentType: parsed.data.photo.contentType, deletedAt: null },
        };
        state.idChecks = [made, ...state.idChecks];
        return json(201, made);
      }
      case 'GET wallet/balance':
        // As the server: the choice rides only this read, not the balance a top-up or an adjustment answers with.
        return json(200, state.reportsWalletUse ? { ...balance(playerId), useForPlay: !state.walletOff.has(playerId) } : balance(playerId));
      case 'GET wallet':
        return page(PlayerHistoryPageQuerySchema, statement(playerId));
      case 'POST wallet/top-ups':
        return topUp(playerId, input);
      case 'POST wallet/adjustments':
        return adjust(playerId, input);
      case 'POST wallet/refunds':
        return refund(playerId, input);
      // The history tabs (spec/13 §8): a little seeded history so the console's tabs render their rows.
      case 'GET sessions':
        return page(PageQuerySchema, [
          { id: 'ses_01k5znw4hmfay8vvr14d2pf5a1', venueId: VENUE_ID, startedAt: '2026-09-25T18:00:00.000Z', endedAt: '2026-09-25T20:30:00.000Z', stationNumber: 4, status: 'ended', playedSeconds: 9000, endReason: 'logout' },
          { id: 'ses_01k5znw4hmfay8vvr14d2pf5a2', venueId: VENUE_ID, startedAt: NOW, endedAt: null, stationNumber: 2, status: 'active', playedSeconds: 600, endReason: null },
        ]);
      // The Games tab (F-CAT-04): per-title totals and the newest plays.
      case 'GET games':
        return json(200, {
          totalSeconds: 5400,
          titles: [
            { titleId: 'gam_01k5znw4hmfay8vvr14d2pf5e1', name: 'Valorant', seconds: 3000, sessions: 2, lastPlayedAt: '2026-09-25T20:00:00.000Z' },
            { titleId: 'gam_01k5znw4hmfay8vvr14d2pf5e2', name: 'Counter-Strike 2', seconds: 2400, sessions: 1, lastPlayedAt: '2026-09-24T19:00:00.000Z' },
          ],
          recent: [
            { id: 'gpl_01k5znw4hmfay8vvr14d2pf5g1', venueId: VENUE_ID, sessionId: 'ses_01k5znw4hmfay8vvr14d2pf5a1', stationNumber: 4, titleId: 'gam_01k5znw4hmfay8vvr14d2pf5e1', name: 'Valorant', startedAt: '2026-09-25T19:10:00.000Z', endedAt: '2026-09-25T20:00:00.000Z', seconds: 3000 },
          ],
        });
      case 'GET orders':
        return page(PageQuerySchema, [{ id: 'fod_01k5znw4hmfay8vvr14d2pf5b1', venueId: VENUE_ID, placedAt: '2026-09-25T18:30:00.000Z', total: { amount: 34000, currency: 'INR' }, channel: 'counter', status: 'paid', note: null }]);
      case 'GET passes':
        return page(PageQuerySchema, [{ id: 'psh_01k5znw4hmfay8vvr14d2pf5c1', name: 'Hourly', grantedSeconds: 3600, remainingSeconds: 1800, expiresAt: '2026-12-31T00:00:00.000Z', status: 'active', purchasedAt: '2026-09-20T10:00:00.000Z' }]);
      case 'GET points':
        return page(PageQuerySchema, [{ id: 'pts_01k5znw4hmfay8vvr14d2pf5d1', occurredAt: NOW, venueId: VENUE_ID, points: 50 }]);
      default:
        return undefined;
    }
  }

  return { handle, state, spend, creditOnline, creditRefund, grantBonus };
}

export type FakePlayers = ReturnType<typeof createFakePlayers>;
